import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { todayWIB } from "@/components/staff/attendance/utils";
import { OutletCard } from "@/components/staff/DailyReport";
import { SalesCharts } from "@/components/staff/SalesCharts";
import { MonthCompare, ItemRanking } from "@/components/staff/MonthlyExtras";

const COLS = ["Tanggal", "Gerai", "Lunas", "Subtotal", "Potongan", "DPP", "PB1", "Total"];

export const MonthlyReport = ({ outlets }) => {
  const [month, setMonth] = useState(todayWIB().slice(0, 7));
  const [outlet, setOutlet] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/reports/monthly", { params: { month, outlet } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month, outlet]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const header = ["Tanggal", "Gerai", "Pesanan Lunas", "Pesanan Batal", "Subtotal", "Potongan Coin", "DPP", "PB1 10%", "Kode Unik", "Total Diterima", "QRIS", "Transfer Bank"];
    const line = (date, s) => [date, s.outlet_code, s.orders_paid, s.orders_cancelled, s.subtotal, s.discount, s.dpp, s.tax, s.unique_code, s.total, s.qris, s.bank];
    const rows = data.daily.map((d) => line(d.date, d));
    rows.push([]);
    rows.push([`REKAP BULAN ${data.month} (hanya pesanan lunas)`]);
    data.outlets.forEach((s) => rows.push(line(data.month, s)));
    rows.push([]);
    rows.push(["PERBANDINGAN BULAN LALU", "Gerai", "Bulan lalu", "Penjualan bulan lalu", "Penjualan bulan ini", "PB1 bulan lalu", "PB1 bulan ini"]);
    data.outlets.forEach((s) => rows.push(["", s.outlet_code, s.prev.month, s.prev.total, s.total, s.prev.tax, s.tax]));
    rows.push([]);
    rows.push(["10 MENU TERLARIS", "Gerai", "Peringkat", "Menu", "Terjual", "Pendapatan"]);
    data.outlets.forEach((s) => s.top_items.forEach((it, i) => rows.push(["", s.outlet_code, i + 1, it.name, it.qty, it.revenue])));
    rows.push([]);
    rows.push(["MENU JARANG LAKU", "Gerai", "Urutan", "Menu", "Terjual", "Pendapatan"]);
    data.outlets.forEach((s) => s.slow_items.forEach((it, i) => rows.push(["", s.outlet_code, i + 1, it.name, it.qty, it.revenue])));
    downloadCsv(`laporan-bulanan-${outlet || "semua-gerai"}-${data.month}.csv`, header, rows);
  };

  return (
    <div data-testid="monthly-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif font-semibold text-2xl">Laporan bulanan gerai</h2>
          <p className="text-sm text-muted2">Rekap penjualan & PB1 per gerai untuk pelaporan pajak daerah.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="monthly-report-month-input" />
          <select value={outlet} onChange={(e) => setOutlet(e.target.value)} className="px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="monthly-report-outlet-filter">
            <option value="">Semua gerai</option>
            {outlets.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
          </select>
          <button onClick={exportCsv} disabled={!data || !data.outlets.length} className="btn-forest px-4 py-2 text-sm flex items-center gap-2" data-testid="monthly-report-download-button"><Download className="w-4 h-4" /> Unduh CSV</button>
        </div>
      </div>
      {data && data.outlets.length === 0 && <p className="text-center text-muted2 py-12" data-testid="monthly-report-empty">Belum ada transaksi pada bulan ini.</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">{data?.outlets.map((s) => <OutletCard key={s.outlet_code} s={s}><MonthCompare s={s} /></OutletCard>)}</div>
      {data?.daily.length > 0 && (
        <div className="card-paper mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="monthly-report-table">
            <thead><tr className="text-left text-xs text-muted2 border-b border-sage-200">{COLS.map((h) => <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-sage-200">
              {data.daily.map((d) => (
                <tr key={`${d.date}-${d.outlet_code}`} data-testid={`monthly-row-${d.date}-${d.outlet_code}`}>
                  <td className="px-4 py-2.5 whitespace-nowrap">{d.date}</td><td className="px-4 py-2.5">{d.outlet_code}</td><td className="px-4 py-2.5">{d.orders_paid}</td>
                  <td className="px-4 py-2.5">{rupiah(d.subtotal)}</td><td className="px-4 py-2.5">{d.discount ? `-${rupiah(d.discount)}` : "–"}</td><td className="px-4 py-2.5">{rupiah(d.dpp)}</td>
                  <td className="px-4 py-2.5">{rupiah(d.tax)}</td><td className="px-4 py-2.5 font-semibold">{rupiah(d.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.outlets.length > 0 && <ItemRanking outlets={data.outlets} />}
      {data?.outlets.length > 0 && <ItemRanking outlets={data.outlets} field="slow_items" title="Menu jarang laku per gerai" testid="slow-items" slow />}
      {data?.outlets.length > 0 && <SalesCharts data={data} />}
    </div>
  );
};
